import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { countAudience, uploadList } from "../../Helpers/campaignApi";
import { CUSTOMER_RULES, KYC_LABEL, STAFF_RULES } from "./shared";

// Step 1 of the wizard: who gets it (Phase 8.2 rules, combined with AND), or an uploaded list for
// customers, or picked staff. The count and 10 sample names update as the rules change.
const defaultValue = (kind) => (kind === "days" ? 30 : kind === "range" ? { min: 1, max: null } : kind === "kyc" ? ["verified"] : ["hubs", "cities", "roles", "departments"].includes(kind) ? [] : undefined);

const MultiPick = ({ options, value = [], onChange, label }) => (
    <select multiple className="form-select form-select-sm" style={{ minHeight: 90 }} aria-label={label} value={value} onChange={(e) => onChange([...e.target.selectedOptions].map((o) => o.value))}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
);

const RuleValue = ({ rule, kind, options, onChange }) => {
    if (kind === "none") return <span className="small text-muted">-</span>;
    if (kind === "days") return <input type="number" min={1} max={3650} className="form-control form-control-sm" style={{ maxWidth: 120 }} value={rule.value ?? ""} onChange={(e) => onChange({ value: e.target.value === "" ? "" : Number(e.target.value) })} aria-label="Days" />;
    if (kind === "range") {
        return (
            <div className="d-flex gap-2 align-items-center">
                <input type="number" min={0} className="form-control form-control-sm" style={{ maxWidth: 90 }} placeholder="min" value={rule.value?.min ?? ""} onChange={(e) => onChange({ value: { ...rule.value, min: e.target.value === "" ? null : Number(e.target.value) } })} aria-label="At least" />
                <span className="small">to</span>
                <input type="number" min={0} className="form-control form-control-sm" style={{ maxWidth: 90 }} placeholder="max" value={rule.value?.max ?? ""} onChange={(e) => onChange({ value: { ...rule.value, max: e.target.value === "" ? null : Number(e.target.value) } })} aria-label="At most" />
                <span className="small text-muted">trips</span>
            </div>
        );
    }
    if (kind === "kyc") return <MultiPick label="KYC status" options={Object.entries(KYC_LABEL).map(([value, label]) => ({ value, label }))} value={[].concat(rule.value || [])} onChange={(value) => onChange({ value })} />;
    const lists = {
        hubs: (options.hubs || []).map((h) => ({ value: h.hubId, label: `${h.name}${h.city ? ` (${h.city})` : ""}` })),
        cities: (options.cities || []).map((c) => ({ value: c, label: c })),
        roles: (options.roles || []).map((r) => ({ value: r.roleId, label: r.name })),
        departments: (options.departments || []).map((d) => ({ value: d.departmentId, label: d.name })),
    };
    return (
        <div className="d-flex gap-2 align-items-start flex-wrap">
            <div style={{ minWidth: 220 }}><MultiPick label={rule.field} options={lists[kind] || []} value={[].concat(rule.value || [])} onChange={(value) => onChange({ value })} /></div>
            {["hub", "city"].includes(rule.field) && (
                <select className="form-select form-select-sm w-auto" value={rule.op === "last" ? "last" : "any"} onChange={(e) => onChange({ op: e.target.value })} aria-label="Which booking">
                    <option value="any">any past booking</option>
                    <option value="last">the last booking</option>
                </select>
            )}
        </div>
    );
};

const AudienceStep = ({ draft, setDraft, options, trigger }) => {
    const [count, setCount] = useState(null);
    const [counting, setCounting] = useState(false);
    const [upload, setUpload] = useState(null);
    const staff = draft.audienceType === "staff";
    const RULES = staff ? STAFF_RULES : CUSTOMER_RULES;
    const rules = draft.segment?.rules || [];
    const useList = !staff && !!draft.uploadId;

    const setRules = (next) => setDraft({ segment: { logic: "AND", rules: next } });
    const addRule = (field) => {
        const def = RULES.find((r) => r.field === field);
        if (!def || rules.some((r) => r.field === field)) return;
        setRules([...rules, { field, op: ["hub", "city"].includes(field) ? "any" : "is", value: defaultValue(def.kind) }]);
    };
    const changeRule = (index, patch) => setRules(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    const removeRule = (index) => setRules(rules.filter((_, i) => i !== index));

    // The live count, half a second after the last change (a trigger campaign's people come from its trigger).
    useEffect(() => {
        if (trigger) { setCount(null); return undefined; }
        const has = rules.length || draft.uploadId || (draft.staffIds || []).length;
        if (!has) { setCount(null); return undefined; }
        const timer = setTimeout(async () => {
            setCounting(true);
            const result = await countAudience({ audienceType: draft.audienceType, segment: { rules }, uploadId: draft.uploadId || null, staffIds: draft.staffIds || [] });
            setCounting(false);
            setCount(result.ok ? result.data : { error: result.message });
        }, 500);
        return () => clearTimeout(timer);
    }, [draft.audienceType, JSON.stringify(rules), draft.uploadId, JSON.stringify(draft.staffIds), trigger]); // eslint-disable-line

    const readFile = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) { toast.error("The file is too big (5 MB at most)"); return; }
        const text = await file.text();
        const result = await uploadList({ fileName: file.name, text, kind: "auto" });
        if (!result.ok) { toast.error(result.message); return; }
        setUpload(result.data.upload);
        setDraft({ uploadId: result.data.upload.uploadId, segment: { logic: "AND", rules: [] } });
        toast.success(`${result.data.upload.matched} of ${result.data.upload.rows} rows matched a customer`);
    };

    return (
        <div>
            <div className="row g-3 mb-3">
                <div className="col-md-4">
                    <label className="form-label small" htmlFor="cw-type">Send to</label>
                    <select id="cw-type" className="form-select" value={draft.audienceType} onChange={(e) => setDraft({ audienceType: e.target.value, segment: { logic: "AND", rules: [] }, uploadId: null, staffIds: [], purpose: e.target.value === "staff" ? "service" : draft.purpose, channels: e.target.value === "staff" ? ["push"] : draft.channels })}>
                        <option value="customer">Customers</option>
                        <option value="staff">Staff</option>
                    </select>
                </div>
                {!staff && !trigger && (
                    <div className="col-md-8">
                        <span className="form-label small d-block">Choose by</span>
                        <div className="btn-group" role="group" aria-label="Choose customers by">
                            <button type="button" className={`nc-pill ${!useList ? "is-active" : ""}`} onClick={() => setDraft({ uploadId: null })}>Rules</button>
                            <button type="button" className={`nc-pill ${useList ? "is-active" : ""}`} onClick={() => document.getElementById("cw-file")?.click()}>Upload a list (CSV)</button>
                        </div>
                        <input id="cw-file" type="file" accept=".csv,text/csv,.txt" className="d-none" onChange={readFile} />
                    </div>
                )}
            </div>

            {trigger && <div className="alert alert-light border small">A trigger campaign goes to the customers who reach its trigger each day (set on the Schedule step). Rules here only narrow it, for example to one city.</div>}

            {useList ? (
                <div className="border rounded-3 p-3 mb-3">
                    <div className="fw-semibold small mb-1">Uploaded list</div>
                    {upload ? (
                        <>
                            <div className="small">{upload.fileName}: {upload.rows} rows, <strong>{upload.matched}</strong> matched a customer.</div>
                            {upload.unmatched?.length ? (
                                <details className="small mt-1"><summary>{upload.unmatched.length} not matched</summary>
                                    <ul className="mb-0">{upload.unmatched.slice(0, 50).map((u) => <li key={`${u.row}-${u.value}`}>Row {u.row}: {u.value} ({u.reason.replace("_", " ")})</li>)}</ul>
                                </details>
                            ) : null}
                        </>
                    ) : <div className="small text-muted">List {draft.uploadId} is attached.</div>}
                    <button type="button" className="btn btn-link btn-sm px-0" onClick={() => document.getElementById("cw-file")?.click()}>Upload another</button>
                </div>
            ) : (
                <>
                    {rules.map((rule, index) => {
                        const def = RULES.find((r) => r.field === rule.field) || { label: rule.field, kind: "none" };
                        return (
                            <div key={rule.field} className="d-flex flex-wrap gap-2 align-items-start border rounded-3 p-2 mb-2 bg-white">
                                <div className="fw-semibold small pt-1" style={{ minWidth: 220 }}>{index > 0 ? "and " : ""}{def.label}</div>
                                <div className="flex-grow-1"><RuleValue rule={rule} kind={def.kind} options={options || {}} onChange={(patch) => changeRule(index, patch)} /></div>
                                <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => removeRule(index)} aria-label={`Remove ${def.label}`}>Remove</button>
                            </div>
                        );
                    })}
                    <select className="form-select form-select-sm w-auto mb-3" value="" onChange={(e) => addRule(e.target.value)} aria-label="Add a rule">
                        <option value="">+ Add a rule</option>
                        {RULES.filter((r) => !rules.some((x) => x.field === r.field)).map((r) => <option key={r.field} value={r.field}>{r.label}</option>)}
                    </select>
                    {staff && (
                        <div className="mb-3">
                            <label className="form-label small" htmlFor="cw-staff">Also these staff (IDs, separated by commas)</label>
                            <input id="cw-staff" className="form-control form-control-sm" value={(draft.staffIds || []).join(", ")} onChange={(e) => setDraft({ staffIds: e.target.value.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean) })} />
                        </div>
                    )}
                </>
            )}

            {!trigger && (
                <div className="border rounded-3 p-3 bg-light" aria-live="polite">
                    {counting ? <span className="small text-muted">Counting...</span>
                        : count?.error ? <span className="small text-danger">{count.error}</span>
                            : count ? (
                                <>
                                    <div className="fw-semibold">{count.count.toLocaleString("en-IN")} {staff ? "staff" : "customers"} match</div>
                                    {count.sample?.length ? <div className="small text-muted mt-1">{count.sample.map((p) => `${p.name}${p.phone ? ` (${p.phone})` : ""}`).join(", ")}{count.count > count.sample.length ? " ..." : ""}</div> : null}
                                    <div className="small text-muted mt-1">Consent and channel rules are applied on the Review step.</div>
                                </>
                            ) : <span className="small text-muted">Add a rule{staff ? " or staff IDs" : " or upload a list"} to see how many people it reaches.</span>}
                </div>
            )}
        </div>
    );
};

export default AudienceStep;
